import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  COMPASS_BEARING_DEG,
  COMPASS_DIRECTIONS,
  compassDirectionFromHeading,
  normalizeHeadingDeg,
  type CompassDirection,
} from './compass';
import { findAncestorAcrossShadow } from '../util/shadowDom';
import type { SatApp } from '../App';

/**
 * Phase 7 chunk 2C — screen-space overlay for sky view.
 *
 * Rendered absolutely-positioned inside `.globe-area`. Visible only
 * when `<sat-app>` is in viewMode='sky'. Surfaces two affordances the
 * topbar toggle alone doesn't provide:
 *
 *  1. A floating `↓ Back to globe` button anchored top-left of the
 *     globe area — large, always-on-screen, never under a popover.
 *  2. A horizontal compass strip below the back button showing the
 *     8 cardinal/intercardinal directions with the current camera
 *     heading highlighted.
 *
 * The element subscribes to two window events:
 *  - `view-mode-changed` (dispatched by <sat-app>.requestViewMode) →
 *    show/hide the overlay.
 *  - `sky-camera-heading` (dispatched by Globe while sky view is
 *    active) → updates the highlighted compass segment.
 *
 * Click on the back button walks up to <sat-app> via the shadow-DOM-
 * aware ancestor helper and calls requestViewMode('globe') — same
 * code path as the topbar toggle.
 */
@customElement('sat-sky-overlay')
export class SatSkyOverlay extends LitElement {
  @state() private viewMode: 'globe' | 'sky' = 'globe';
  /** Current camera heading in degrees, normalized to [0, 360). */
  @state() private headingDeg = 0;

  private viewModeListener = (e: Event): void => {
    const detail = (e as CustomEvent<{ viewMode: 'globe' | 'sky' }>).detail;
    if (detail && (detail.viewMode === 'globe' || detail.viewMode === 'sky')) {
      this.viewMode = detail.viewMode;
    }
  };

  private headingListener = (e: Event): void => {
    const detail = (e as CustomEvent<{ headingDeg: number }>).detail;
    if (detail && Number.isFinite(detail.headingDeg)) {
      this.headingDeg = normalizeHeadingDeg(detail.headingDeg);
    }
  };

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('view-mode-changed', this.viewModeListener);
    window.addEventListener('sky-camera-heading', this.headingListener);
    // Pick up the current mode in case the overlay mounts after a
    // URL-restored sky-view activation already fired the event.
    const app = findAncestorAcrossShadow(this, 'sat-app') as SatApp | null;
    if (app !== null) {
      this.viewMode = app.getViewMode();
    }
  }

  disconnectedCallback(): void {
    window.removeEventListener('view-mode-changed', this.viewModeListener);
    window.removeEventListener('sky-camera-heading', this.headingListener);
    super.disconnectedCallback();
  }

  static styles = css`
    :host {
      position: absolute;
      top: 1rem;
      left: 1rem;
      z-index: 25;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      font-family: var(--font-mono);
      pointer-events: none; /* allow Cesium below to receive drags */
    }
    /* Children get their pointer events back so they're still clickable. */
    :host > * { pointer-events: auto; }

    /* Visibility gate — only render when sky view is active. */
    :host([data-hidden]) { display: none; }

    .back {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.85rem;
      background: var(--color-bg-overlay);
      color: var(--color-accent);
      border: 1px solid var(--color-accent);
      border-radius: 4px;
      font-family: var(--font-mono);
      font-size: 0.9rem;
      cursor: pointer;
      backdrop-filter: blur(8px);
      transition: background 0.1s ease;
    }
    .back:hover {
      background: rgba(0, 217, 255, 0.12);
    }
    .back__glyph { font-size: 1rem; line-height: 1; }

    .compass {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      padding: 0.4rem 0.6rem;
      background: var(--color-bg-overlay);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      font-family: var(--font-mono);
      font-size: 0.75rem;
      color: var(--color-text-muted);
      backdrop-filter: blur(8px);
    }
    /* Compass cells are buttons: click-to-aim the camera at that
       direction (heading change only; pitch + FOV preserved). */
    .compass__cell {
      min-width: 1.6rem;
      text-align: center;
      padding: 0.1rem 0.25rem;
      border-radius: 2px;
      /* Reset native button chrome so they read as topbar-style chips. */
      background: transparent;
      color: inherit;
      border: 1px solid transparent;
      font: inherit;
      cursor: pointer;
    }
    .compass__cell:hover {
      color: var(--color-accent);
      background: rgba(0, 217, 255, 0.08);
    }
    .compass__cell:focus-visible {
      outline: none;
      border-color: var(--color-accent);
    }
    .compass__cell[data-active='true'] {
      color: var(--color-accent);
      background: rgba(0, 217, 255, 0.12);
      font-weight: 500;
    }
    .compass__bearing {
      margin-left: 0.5rem;
      padding-left: 0.5rem;
      border-left: 1px dashed var(--color-border);
      color: var(--color-text-dim);
      font-size: 0.7rem;
    }
  `;

  updated(changed: Map<string, unknown>): void {
    if (changed.has('viewMode')) {
      if (this.viewMode === 'sky') this.removeAttribute('data-hidden');
      else this.setAttribute('data-hidden', '');
    }
  }

  private handleBackClick(): void {
    const app = findAncestorAcrossShadow(this, 'sat-app') as SatApp | null;
    if (app === null) return;
    app.requestViewMode('globe');
  }

  private handleCompassClick(direction: CompassDirection): void {
    const app = findAncestorAcrossShadow(this, 'sat-app') as SatApp | null;
    if (app === null) return;
    app.requestSkyHeadingDeg(COMPASS_BEARING_DEG[direction]);
  }

  render() {
    const active: CompassDirection = compassDirectionFromHeading(this.headingDeg);
    const bearingLabel = `${Math.round(this.headingDeg)}°`;
    return html`
      <button
        type="button"
        class="back"
        @click=${this.handleBackClick}
        title="Return to globe view"
        aria-label="Back to globe view"
      >
        <span class="back__glyph" aria-hidden="true">↓</span>
        <span>Back to globe</span>
      </button>
      <div class="compass" role="toolbar" aria-label="Camera heading — click a direction to aim">
        ${COMPASS_DIRECTIONS.map((d) => html`
          <button
            type="button"
            class="compass__cell"
            data-active=${d === active ? 'true' : 'false'}
            title=${`Aim ${d} (heading ${COMPASS_BEARING_DEG[d]}°)`}
            aria-label=${`Aim ${d}, heading ${COMPASS_BEARING_DEG[d]} degrees`}
            aria-pressed=${d === active ? 'true' : 'false'}
            @click=${() => this.handleCompassClick(d)}
          >${d}</button>
        `)}
        <span class="compass__bearing" aria-hidden="true">${bearingLabel}</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-sky-overlay': SatSkyOverlay;
  }
}
